import { Box } from "@mui/material";
import {
  VALID_FORM_CODES,
  VALID_MANAGEMENT_METHOD_CODES,
  VALID_SOURCE_CODES,
} from "encamp-shared/src/waste/validCodes";
import { WasteClassification } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";

import { prettyPrintWasteClassification } from "./Facilities/Facility/prettyPrintWasteClassification";
import { WasteClassificationChip } from "./WasteClassificationChip";
import { WasteStreamAmountCell, WasteStreamAmountHeader } from "./WasteStreamAmountCell";
import {
  WS_DISCREPANCY_FIELD_LABELS,
  WasteStreamDiscrepancyRow,
} from "./wasteStreamDiscrepancyIndicator";
import { WasteStreamFieldWarning } from "./WasteStreamFieldWarning";

/**
 * The waste-stream fields shared by the tenant and facility grids' code/amount
 * columns. Kept minimal so both generated row types satisfy it.
 */
export interface WasteStreamCodeColumnRow {
  federalWasteCodes?: string[] | null;
  stateWasteCodes?: string[] | null;
  classification?: WasteClassification | null;
  formCode?: string | null;
  sourceCode?: string | null;
  managementMethodCodes: string[];
  vendorAmount?: number | null;
  eManifestAmount?: number | null;
  documentAmount?: number | null;
  effectiveAmount?: number | null;
  discrepancies: ReadonlyArray<WasteStreamDiscrepancyRow>;
}

/**
 * The waste-code / classification / amount columns shared verbatim by the tenant
 * (`TenantWasteStreams`) and facility (`WasteStreamsDataGrid`) grids: Federal /
 * State Waste Code, Classification, Form / Source / Management Method Code, and
 * Amount — each with its per-cell discrepancy indicator. Defined once here so an
 * edit lands in both grids instead of drifting between two hand-kept copies.
 *
 * `showDiscrepancies` gates the (!) indicators (the flag is staff/add-on driven
 * in each grid). `isStaff` scopes which discrepancy statuses each indicator
 * treats as open (staff see PENDING + UNRESOLVED, non-staff see PENDING only).
 * Grid-specific columns (Facility, Waste Stream, Registration, Issues) stay in
 * the grids since they diverge.
 */
export function buildWasteStreamCodeColumns<Row extends WasteStreamCodeColumnRow>(
  showDiscrepancies: boolean,
  isStaff: boolean
): OmnisearchGridColDef<Row>[] {
  return [
    {
      field: "federalWasteCodes",
      headerName: "Federal Waste Code",
      flex: 0.5,
      sortable: true,
      valueGetter: (_, row) => (row.federalWasteCodes || []).join(", "),
      renderCell: ({ value, row }) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </Box>
          {showDiscrepancies && (
            <WasteStreamFieldWarning
              discrepancies={row.discrepancies}
              field="federalWasteCodes"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.federalWasteCodes}
              inline
              isStaff={isStaff}
            />
          )}
        </Box>
      ),
      headerClassName: "wrapHeader",
    },
    {
      field: "stateWasteCodes",
      headerName: "State Waste Code",
      flex: 0.5,
      sortable: true,
      valueGetter: (_, row) => (row.stateWasteCodes || []).join(", "),
      renderCell: ({ value, row }) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </Box>
          {showDiscrepancies && (
            <WasteStreamFieldWarning
              discrepancies={row.discrepancies}
              field="stateWasteCodes"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.stateWasteCodes}
              includeFieldIssues
              inline
              isStaff={isStaff}
            />
          )}
        </Box>
      ),
      headerClassName: "wrapHeader",
    },
    {
      field: "classification",
      headerName: "Classification",
      flex: 0.5,
      filterable: false,
      sortable: true,
      // valueFormatter keeps a readable string for copy / export / screen
      // readers; the chip is the visual.
      valueFormatter: (_, row) => prettyPrintWasteClassification(row.classification),
      renderCell: ({ row }) => <WasteClassificationChip classification={row.classification} />,
      headerClassName: "wrapHeader",
    },
    {
      field: "formCode",
      headerName: "Form Code",
      filterKeyType: "enumAutocomplete",
      enumValues: [...VALID_FORM_CODES],
      flex: 0.3,
      sortable: true,
      renderCell: ({ value, row }) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </Box>
          {showDiscrepancies && (
            <WasteStreamFieldWarning
              discrepancies={row.discrepancies}
              field="formCode"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.formCode}
              inline
              isStaff={isStaff}
            />
          )}
        </Box>
      ),
      headerClassName: "wrapHeader",
    },
    {
      field: "sourceCode",
      headerName: "Source Code",
      filterKeyType: "enumAutocomplete",
      enumValues: [...VALID_SOURCE_CODES],
      flex: 0.3,
      sortable: true,
      renderCell: ({ value, row }) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </Box>
          {showDiscrepancies && (
            <WasteStreamFieldWarning
              discrepancies={row.discrepancies}
              field="sourceCode"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.sourceCode}
              inline
              isStaff={isStaff}
            />
          )}
        </Box>
      ),
      headerClassName: "wrapHeader",
    },
    {
      field: "managementMethodCodes",
      headerName: "Management Method Code",
      filterKeyType: "enumAutocomplete",
      enumValues: [...VALID_MANAGEMENT_METHOD_CODES],
      flex: 0.5,
      sortable: true,
      valueGetter: (_, row) => row.managementMethodCodes.join(", "),
      renderCell: ({ value, row }) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </Box>
          {showDiscrepancies && (
            <WasteStreamFieldWarning
              discrepancies={row.discrepancies}
              field="managementMethodCode"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.managementMethodCode}
              inline
              isStaff={isStaff}
            />
          )}
        </Box>
      ),
      headerClassName: "wrapHeader",
    },
    {
      // Canonical effective amount with a per-source tooltip. Not filterable:
      // effectiveAmount is a server aggregate.
      field: "effectiveAmount",
      headerName: "Amount",
      flex: 0.4,
      filterable: false,
      sortable: true,
      headerClassName: "wrapHeader",
      renderHeader: () => <WasteStreamAmountHeader />,
      // WasteStreamAmountCell surfaces the per-source amounts on hover when the
      // raw values differ. The (!) is driven off persisted quantity-discrepancy
      // rows only — it flags the case the amount cell can't (a flagged
      // discrepancy whose consolidated amounts happen to match), without
      // doubling up on the raw-disagreement signal.
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <WasteStreamAmountCell
            amount={row.effectiveAmount}
            vendorAmount={row.vendorAmount}
            eManifestAmount={row.eManifestAmount}
            documentAmount={row.documentAmount}
          />
          {showDiscrepancies && (
            <WasteStreamFieldWarning
              discrepancies={row.discrepancies}
              field="quantity"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.quantity}
              inline
              isStaff={isStaff}
            />
          )}
        </Box>
      ),
    },
  ];
}
